import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, ExternalLink, Copy, Files, Trash2, FileText, LayoutTemplate, Archive, ArchiveRestore, ShieldCheck, Download, ClipboardCopy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { errMessage } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { makeQR, scaledQuietZone } from "@/lib/qr";
import { referencePageUrl, referenceQrUrl } from "@/lib/publicUrl";
import { ProjectAnalytics } from "@/components/analytics/ProjectAnalytics";

// Build makeQR options from the saved QR style, scaling the quiet zone to the output size.
function qrOptsFromStyle(style, size) {
  const s = style || {};
  return {
    size,
    fgColor: s.fgColor || "#0A2540",
    bgColor: s.bgColor || "#FFFFFF",
    dotType: s.dotType || "square",
    cornerType: s.cornerType || "square",
    errorCorrectionLevel: s.ecLevel || "M",
    logo: s.logo || null,
    margin: scaledQuietZone(s.margin ?? 16, 300, size),
  };
}

// Small QR thumbnail + download + copy for a published page's permanent /r/{public_id} URL,
// using the exact colors/logo the user picked in the editor's QR Studio.
function CardQR({ url, name, style }) {
  const holder = useRef(null);
  const safeName = `citeqr-${(name || "reference").slice(0, 50).replace(/[^\w-]+/g, "-")}`;
  useEffect(() => {
    if (!holder.current) return;
    holder.current.innerHTML = "";
    makeQR({ data: url, ...qrOptsFromStyle(style, 84) }).append(holder.current);
  }, [url, style]);
  const download = async () => {
    try {
      const qr = makeQR({ data: url, ...qrOptsFromStyle(style, 2048) });
      await qr.download({ name: safeName, extension: "png" });
    } catch (e) { toast.error("Couldn't download the QR code."); }
  };
  const downloadSvg = async () => {
    try {
      const qr = makeQR({ data: url, ...qrOptsFromStyle(style, 2048), type: "svg" });
      await qr.download({ name: safeName, extension: "svg" });
    } catch (e) { toast.error("Couldn't download the SVG."); }
  };
  const copyImage = async () => {
    try {
      const qr = makeQR({ data: url, ...qrOptsFromStyle(style, 1024) });
      const blob = await qr.getRawData("png");
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("QR image copied — paste into PowerPoint, Word, or a poster.");
    } catch (e) { toast.error("Couldn't copy the QR image. Try Download instead."); }
  };
  return (
    <div className="mt-4 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <div ref={holder} className="h-[84px] w-[84px] shrink-0 overflow-hidden rounded bg-white [&>canvas]:h-full [&>canvas]:w-full" data-testid="card-qr-thumb" aria-label="QR code preview" />
      <div className="min-w-0 flex-1">
        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700" data-testid="qr-unchanged-badge"><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Same link since publish</Badge>
        <p className="mt-1 truncate font-mono text-[11px] text-slate-500">/r/{url.split("/r/")[1].split("?")[0]}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={download} data-testid="card-qr-download"><Download className="mr-1.5 h-3.5 w-3.5" /> PNG</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={downloadSvg} data-testid="card-qr-download-svg"><Download className="mr-1.5 h-3.5 w-3.5" /> SVG</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyImage} data-testid="card-qr-copy"><ClipboardCopy className="mr-1.5 h-3.5 w-3.5" /> Copy QR</Button>
        </div>
      </div>
    </div>
  );
}

export default function SavedProjects() {
  const [projects, setProjects] = useState(null);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/analytics/projects?days=30").then((r) => setStats(r.data.projects)).catch(() => setStats({})); }, []);

  useEffect(() => {
    document.title = "Saved Projects — CiteQR";
    load();
  }, []);

  const duplicate = async (id) => {
    try { await api.post(`/projects/${id}/duplicate`); toast.success("Project duplicated."); load(); }
    catch (e) { toast.error(errMessage(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/projects/${id}`); toast.success("Project deleted."); load(); }
    catch (e) { toast.error(errMessage(e)); }
  };
  const archive = async (id) => {
    try { await api.post(`/projects/${id}/archive`); toast.success("Project archived — its QR link is reserved and can be restored anytime."); load(); }
    catch (e) { toast.error(errMessage(e)); }
  };
  const unarchive = async (id) => {
    try { await api.post(`/projects/${id}/unarchive`); toast.success("Project restored on the same QR link."); load(); }
    catch (e) { toast.error(errMessage(e)); }
  };
  const copyLink = (pid) => copyText(referencePageUrl(pid), "Link copied.");

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 fade-in">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Saved projects</h1>
          <p className="mt-2 text-base text-slate-600">Organize, edit, duplicate, archive, or delete your reference pages.</p>
        </div>
        <Button asChild className="bg-[#0D9488] text-white hover:bg-[#0F766E]"><Link to="/reference/new"><Plus className="mr-2 h-4 w-4" /> New reference page</Link></Button>
      </div>

      <div className="mt-8">
        {projects === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0D9488]" /></div>
        ) : projects.length === 0 ? (
          <Card className="border-dashed border-slate-300 bg-slate-50 shadow-none">
            <CardContent className="p-12 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white"><FileText className="h-6 w-6 text-[#0D9488]" /></div>
              <p className="text-base font-medium text-slate-800">No projects yet</p>
              <p className="mt-1 text-sm text-slate-500">Create your first academic reference page to get started.</p>
              <Button asChild className="mt-6 bg-[#0D9488] text-white hover:bg-[#0F766E]"><Link to="/reference/new">Create a reference page</Link></Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
            {projects.map((p) => (
              <Card key={p.id} className="flex flex-col border-slate-200 shadow-sm" data-testid={`project-${p.id}`}>
                <CardContent className="flex flex-1 flex-col p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="line-clamp-2 text-base font-semibold text-slate-900">{p.title}</h3>
                    <Badge
                      variant={p.status === "published" ? "default" : "secondary"}
                      className={p.status === "published" ? "bg-[#0D9488]" : p.status === "archived" ? "border-amber-300 bg-amber-100 text-amber-800" : ""}
                    >{p.status}</Badge>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">{p.references?.length || 0} references · {p.citation_style}</p>
                  {p.author_name && <p className="mt-1 text-xs text-slate-500">{p.author_name}</p>}
                  {p.status === "archived" && (
                    <p className="mt-2 text-xs text-amber-700" data-testid={`archived-note-${p.id}`}>Offline — its <span className="font-mono">/r/{p.public_id}</span> link is reserved. Restore to bring it back on the same QR.</p>
                  )}
                  {p.status === "published" && (
                    <CardQR url={referenceQrUrl(p.public_id)} name={p.title} style={p.qr_style} />
                  )}
                  {p.status !== "draft" && <ProjectAnalytics project={p} summary={stats?.[p.id]} />}

                  <div className="mt-auto flex flex-wrap gap-2 pt-4">
                    <Button asChild size="sm" variant="outline"><Link to={`/reference/${p.id}/edit`} data-testid={`edit-${p.id}`}>Edit</Link></Button>
                    {p.status === "published" && (
                      <>
                        <Button asChild size="sm" variant="ghost"><a href={`/r/${p.public_id}`} target="_blank" rel="noopener noreferrer" aria-label="View" data-testid={`view-${p.id}`}><ExternalLink className="h-4 w-4" /></a></Button>
                        <Button size="sm" variant="ghost" onClick={() => copyLink(p.public_id)} aria-label="Copy link" data-testid={`copy-${p.id}`}><Copy className="h-4 w-4" /></Button>
                        <Button asChild size="sm" variant="ghost"><a href={`/poster/${p.public_id}`} target="_blank" rel="noopener noreferrer" aria-label="Poster Kit" data-testid={`poster-${p.id}`}><LayoutTemplate className="h-4 w-4" /></a></Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => duplicate(p.id)} aria-label="Duplicate" data-testid={`duplicate-${p.id}`}><Files className="h-4 w-4" /></Button>

                    {p.status === "archived" ? (
                      <Button size="sm" variant="outline" onClick={() => unarchive(p.id)} className="text-[#0D9488]" data-testid={`restore-${p.id}`}><ArchiveRestore className="mr-1.5 h-4 w-4" /> Restore</Button>
                    ) : p.status === "published" ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" aria-label="Archive" data-testid={`archive-${p.id}`}><Archive className="h-4 w-4 text-amber-600" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Archive this page?</AlertDialogTitle>
                            <AlertDialogDescription>
                              "{p.title}" will go offline, but its link <span className="font-mono">/r/{p.public_id}</span> stays reserved. Any printed or shared QR codes will show an "archived" notice and start working again the moment you restore it. This is the safe alternative to deleting.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => archive(p.id)} className="bg-amber-600 hover:bg-amber-700" data-testid={`confirm-archive-${p.id}`}>Archive</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : null}

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" aria-label="Delete" data-testid={`delete-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this project permanently?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {p.status === "published" || p.status === "archived" ? (
                              <>This permanently deletes "{p.title}" and frees its link <span className="font-mono">/r/{p.public_id}</span>. <strong className="text-red-600">Any printed or shared QR codes pointing here will stop working forever.</strong> Consider Archive instead if you only want to take it offline. This cannot be undone.</>
                            ) : (
                              <>This permanently deletes "{p.title}". This cannot be undone.</>
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(p.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-${p.id}`}>Delete permanently</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
